/**
 * Workspace homepage config — the business homepage's content abstracted into a
 * config object (matches the Backend Management System homepage model). Rendered
 * by WorkspaceHome behind the CONFIG_DRIVEN_HOMEPAGE_ENABLED stage flag.
 *
 * `loadHomepageConfig(agent)` reads the per-agent config from the Gateway
 * (same endpoint the admin editor writes). Remote data is returned as-is —
 * empty/absent parts are simply not rendered (no default backfill). Only a hard
 * request failure falls back to the built-in default so a Gateway blip can't
 * blank the page.
 */
import { gatewayClient } from '../../client/GatewayClient';
import { appConfig } from '../../config';
import type { Locale } from '../../i18n/resources';

export type HomepageBadge = 'NEW' | 'HOT';

/** Prefix of uploaded-icon S3 keys (see Gateway homepage_icon_store). */
const ICON_KEY_PREFIX = 'homepage-icons/';

/**
 * Resolve a stored icon reference to an <img src>. Uploaded icons are stored as
 * a bare S3 key (`homepage-icons/<agent>/<uuid>.<ext>`) — portable across
 * stages — and read back through the Gateway of the *current* stage. Built-in
 * icons (`/icons/*.svg`) and any absolute URL are returned unchanged.
 */
export function resolveIconSrc(ref?: string): string | undefined {
  if (!ref) return undefined;
  if (ref.startsWith(ICON_KEY_PREFIX)) {
    return `${appConfig.gatewayUrl}/channels/portal/workspace-homepage/icon?key=${encodeURIComponent(ref)}`;
  }
  return ref;
}

/** A colored run of the hero title (data, not markup — no parser, no XSS). */
export interface TitleSegment {
  text: string;
  color?: string;
}

/** Part 2 — a suggestion chip. Click sends `prompt` to the chat. */
export interface HotQuestion {
  id: string;
  uiCopy: string;
  prompt: string;
  badges?: HomepageBadge[];
  /** English text, shown in the English UI; an absent or empty field falls back to the base text. */
  en?: { uiCopy?: string; prompt?: string };
}

/**
 * Allowed Part 3 card links: a site path (`/cheetah`) or an absolute http(s)
 * URL. Anything else — `javascript:`, protocol-relative `//host` (or `/\host`),
 * bare words — is rejected by the editor and ignored by the renderer.
 */
export function isAllowedHref(href?: string): href is string {
  if (!href) return false;
  if (href.startsWith('/')) return !href.startsWith('//') && !href.startsWith('/\\');
  return /^https?:\/\/[^\s/]+/i.test(href);
}

/** Part 3 — a topic card. Click sends `prompt` to chat, or navigates to `href`
 *  when `action === 'navigate'`. */
export interface HotTopic {
  id: string;
  iconUrl?: string;
  /** Admin-only: original uploaded icon file name (for the editor UI; ignored by the renderer). */
  iconFileName?: string;
  title: string;
  description: string;
  prompt?: string;
  badges?: HomepageBadge[];
  action?: 'chat' | 'navigate';
  href?: string;
  /** English text, shown in the English UI; an absent or empty field falls back to the base text. */
  en?: { title?: string; description?: string; prompt?: string };
}

export interface Part1Config {
  /** Preferred: multi-color hero title segments. */
  titleSegments?: TitleSegment[];
  /** Fallback single-string title when segments are absent. */
  title?: string;
  /** Preferred: multi-color subheading segments. */
  subheadingSegments?: TitleSegment[];
  /** Fallback single-string subheading when segments are absent. */
  subheading: string;
  /** English text, shown in the English UI; an absent or empty title / subheading falls back to the base one. */
  en?: {
    titleSegments?: TitleSegment[];
    title?: string;
    subheadingSegments?: TitleSegment[];
    subheading?: string;
  };
}

export interface HomepageConfig {
  agent: string;
  part1?: Part1Config;
  part2?: HotQuestion[];
  part3?: HotTopic[];
  version?: number;
}

// Default content, kept in sync with the Gateway seed (homepage_config_store
// default_config). `en` is the English text; the card prompts have none, so
// English users send the Chinese prompt.
const CARDS: HotTopic[] = [
  {
    id: 'target-attainment',
    title: '我的 quota 能完成吗？',
    description: '看看缺口多大，还需要补多少 pipeline。',
    en: {
      title: 'Will I hit my quota?',
      description: 'See how big the gap is and how much more pipeline you need.',
    },
    iconUrl: '/icons/target-attainment.svg',
    prompt: `执行 Baseline Forecast Report skill，生成报告后给我管理层摘要：
1. 按季度及按年的达成率定位（金额+百分比），
2. 最值得推进的 3-5 个 deal，
3. 最大的结构性风险等，
4. 用户及其下级自己填写forecast时的填写内容可酌情参考。
Skill 可用时优先执行；不可用时基于可查询数据完成等效分析。`,
  },
  {
    id: 'ivt-solution',
    title: '哪些 IVT Solution 能为我创造新机会？',
    description: '为我的客户找到最匹配的 IVT Solution，用已有资产创造新的销售机会。',
    en: {
      title: 'Which IVT Solutions can open new opportunities for me?',
      description: 'Find the IVT Solutions that best fit my customers, and turn existing assets into new sales opportunities.',
    },
    iconUrl: '/icons/daily-briefing.svg',
    badges: ['NEW'],
    prompt: `帮我找出我territory下最适合推进IVT Asset的客户（每个客户最多3个Asset）。

先查我的territory全量客户和行业分布，从IVT全量Asset中匹配：
- 首要看Asset与客户业务场景的匹配度，只保留有明确匹配的客户
- 匹配度相近时，按open opp数+已落地数+scoreX综合排序
- 优先本行业Asset，跨行业适配度明显更高时补充推荐
- 查询客户现有opp和revenue，估算每个推荐可撬动的ARR增量

先输出Executive Summary（最多3条）：①最应优先推进的客户+Asset组合+预估可撬动ARR ②本周该联系的IVT联系人及具体诉求 ③还有哪几个客户值得跟进。

再输出明细表格：客户名/推荐Asset/🏠本行业or🔀跨行业/Asset描述/💡为什么适合(结合该客户具体opp、revenue、业务特征)/💰预估ARR/🎯下一步动作/📧IVT联系人。用🟢🟡🔴标注置信度，匹配度优先、同等匹配度按客户重要性排序。

IVT联系人：AUTO+MFG+MEAD→yingyn@|RCH+FSI+HCLS+GAMES→chuaamz@|EDU→viviannw@|LOG→xiujason@|ENE→hjinamzn@`,
  },
  {
    id: 'latest-news',
    title: '我的客户有什么新动态？',
    description: '新闻、财报、采购信号一次看完。',
    en: {
      title: 'What\'s new with my customers?',
      description: 'News, earnings and buying signals in one place.',
    },
    iconUrl: '/icons/latest-news.svg',
    prompt: `查询我负责客户和所在行业的最新外部动态（新闻、公告、行业趋势），生成情报简报。先判断用户是否属于GAMES(L4), 如果是，执行 daily sales intelligence brief skill 后提炼：
- 客户动态 — 融资/并购/高管变动/新产品发布/财报/裁员等重大事件
- 行业信号 — 政策变化/技术趋势/竞对动作中与我的客户相关的
- 每条标注：🟢 商机信号 / 🟡 值得关注 / 🔴 风险预警
- 每条附：为什么跟我有关 + 建议的跟进动作或对话切入点
聚焦过去 7 天，按业务相关性排序。只呈现与我的客户或行业直接相关的信息，不要泛泛的行业综述。
用户符合条件且Skill 可用时优先执行skill；用户不符合条件或者skill不可用时进行等效分析。`,
  },
  {
    id: 'pipeline-hygiene',
    title: '哪些 deal 正在变冷？',
    description: '找出停滞商机，越早挽回越好。',
    en: {
      title: 'Which deals are going cold?',
      description: 'Find stalled opportunities. The sooner you win them back, the better.',
    },
    iconUrl: '/icons/code-buz.svg',
    prompt: `评估我的 Pipeline 健康度。检查过期未关单、阶段卡顿、长期无更新、漏斗形状异常等。
输出：整体情况评估， 问题商机清单（带SFDC链接），优先处理建议等。`,
  },
  {
    id: 'latest-report',
    title: '本周收入表现如何？',
    description: 'Revenue 趋势、异常、Top 增长/下降客户。',
    en: {
      title: 'How did revenue do this week?',
      description: 'Revenue trends, anomalies, and the top growing and declining customers.',
    },
    iconUrl: '/icons/company-analysis.svg',
    prompt: `执行 Weekly Revenue Report（我的 territory，上一完整周），生成报告后给执行摘要：
1. 一句话定位整体走势，
2. WoW 变化最显著的 Top 3 客户及原因，
3. 需介入的风险客户和扩展机会，
4. 2-3 条优先行动建议。
Skill 可用时优先执行；不可用时基于可查询数据完成等效分析。`,
  },
  {
    id: 'cheetah',
    title: '预约专家',
    description: '一键预约 / 回访 China Tech 团队专家，提升客户对接效率。',
    en: {
      title: 'Book an expert',
      description: 'Book or follow up with China Tech experts in one click, and work with customers faster.',
    },
    iconUrl: '/icons/customer-deep.svg',
    action: 'navigate',
    href: '/cheetah',
  },
];

const QUESTIONS: HotQuestion[] = [
  {
    id: 'q-1',
    uiCopy: '我的 quota 能完成吗？',
    prompt: '我的 quota 能完成吗？',
    badges: ['NEW'],
    en: { uiCopy: 'Will I hit my quota?', prompt: 'Will I hit my quota?' },
  },
  {
    id: 'q-2',
    uiCopy: '哪些 deal 正在变冷？',
    prompt: '哪些 deal 正在变冷？',
    badges: ['HOT'],
    en: { uiCopy: 'Which deals are going cold?', prompt: 'Which deals are going cold?' },
  },
  {
    id: 'q-3',
    uiCopy: '本月有哪些客户 Revenue 出现了明显下降？',
    prompt: '本月有哪些客户 Revenue 出现了明显下降？',
    badges: [],
    en: { uiCopy: 'Which customers saw a clear Revenue drop this month?', prompt: 'Which customers saw a clear Revenue drop this month?' },
  },
  {
    id: 'q-4',
    uiCopy: '最近 30 天新增了哪些商机？',
    prompt: '最近 30 天新增了哪些商机？',
    badges: [],
    en: { uiCopy: 'Which opportunities were created in the last 30 days?', prompt: 'Which opportunities were created in the last 30 days?' },
  },
  {
    id: 'q-5',
    uiCopy: '我应该优先跟进哪些客户？',
    prompt: '我应该优先跟进哪些客户？',
    badges: [],
    en: { uiCopy: 'Which customers should I follow up with first?', prompt: 'Which customers should I follow up with first?' },
  },
  {
    id: 'q-6',
    uiCopy: '帮我做一份我最大客户的全景分析',
    prompt: '帮我做一份我最大客户的全景分析',
    badges: [],
    en: { uiCopy: 'Give me a full picture of my largest customer', prompt: 'Give me a full picture of my largest customer' },
  },
];

export const DEFAULT_HOMEPAGE_CONFIG: HomepageConfig = {
  agent: 'rocky',
  part1: {
    titleSegments: [
      { text: 'Rocky,', color: '#0097a7' },
      { text: ' 你的随身军师' },
    ],
    title: 'Rocky, 你的随身军师',
    subheading: '每天看一眼，上场从容，心里有数',
    en: {
      titleSegments: [
        { text: 'Rocky,', color: '#0097a7' },
        { text: ' your pocket strategist' },
      ],
      title: 'Rocky, your pocket strategist',
      subheading: 'One look a day, and you walk in calm and well prepared',
    },
  },
  part2: QUESTIONS,
  part3: CARDS,
  version: 0,
};

const hasText = (v?: string) => !!v && v.trim() !== '';
const hasSegments = (v?: TitleSegment[]) => !!v && v.some((seg) => hasText(seg.text));
const pick = (en: string | undefined, base: string) => (hasText(en) ? en! : base);

/** One line of the hero (title or subheading) in English, or null to keep the base line. */
function englishLine(segments?: TitleSegment[], text?: string): { segments?: TitleSegment[]; text: string } | null {
  if (hasSegments(segments)) return { segments, text: hasText(text) ? text! : segments!.map((seg) => seg.text).join('') };
  if (hasText(text)) return { segments: undefined, text: text! };
  return null;
}

function localizePart1(p: Part1Config): Part1Config {
  if (!p.en) return p;
  const title = englishLine(p.en.titleSegments, p.en.title);
  const sub = englishLine(p.en.subheadingSegments, p.en.subheading);
  return {
    ...p,
    ...(title ? { titleSegments: title.segments, title: title.text } : {}),
    ...(sub ? { subheadingSegments: sub.segments, subheading: sub.text } : {}),
  };
}

/**
 * The homepage as shown in `locale`. The base text is the Chinese the admin
 * wrote; in the English UI each piece uses its `en` text where that is set and
 * non-empty, and keeps the base text otherwise. zh (and the pre-switch legacy
 * text, whose locale is zh) gets the config unchanged.
 */
export function localizeHomepage(config: HomepageConfig, locale: Locale): HomepageConfig {
  if (locale !== 'en') return config;
  return {
    ...config,
    part1: config.part1 && localizePart1(config.part1),
    part2: config.part2?.map((q) =>
      q.en ? { ...q, uiCopy: pick(q.en.uiCopy, q.uiCopy), prompt: pick(q.en.prompt, q.prompt) } : q,
    ),
    part3: config.part3?.map((card) =>
      card.en
        ? {
            ...card,
            title: pick(card.en.title, card.title),
            description: pick(card.en.description, card.description),
            prompt: hasText(card.en.prompt) ? card.en.prompt : card.prompt,
          }
        : card,
    ),
  };
}

const CONFIG_PATH = '/channels/portal/workspace-homepage';
const DEFAULT_AGENT = 'rocky';

/**
 * Runtime agent (AgentContext `currentAgent`) → homepage-config agent id, i.e.
 * the Gateway `agent` param and DDB partition key `AGENT#<id>`. The business
 * homepage (read) and the RBMS editor (read/write) both resolve through this one
 * map, so the page a user sees is always the one the admin edited.
 *
 * campaign_copilot_agent keeps its own homepage for now; when it is merged into
 * the Marketing Agent, point it at 'marketing_agent' here. Unmapped agents fall
 * back to rocky.
 */
export const HOMEPAGE_CONFIG_AGENT: Record<string, string> = {
  rocky: 'rocky',
  marketing_agent: 'marketing_agent',
  campaign_copilot_agent: 'campaign_copilot_agent',
};

export function homepageConfigAgent(runtimeAgent: string): string {
  return HOMEPAGE_CONFIG_AGENT[runtimeAgent] ?? DEFAULT_AGENT;
}

/**
 * Load an agent's homepage config from the Gateway. Returns remote data as-is
 * (empty parts are left empty → the renderer skips them). On a hard failure
 * (network / auth / 5xx) it falls back to the built-in default so the page is
 * never blank; flip that fallback to `null`/blank here if that's preferred.
 */
export async function loadHomepageConfig(agent: string = DEFAULT_AGENT): Promise<HomepageConfig> {
  try {
    return await gatewayClient.get<HomepageConfig>(CONFIG_PATH, { agent });
  } catch {
    return DEFAULT_HOMEPAGE_CONFIG;
  }
}
